import { addDoc, collection, getCountFromServer, query, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export const COMPONENT_ANALYTICS_EVENTS_COLLECTION = 'componentAnalyticsEvents';
export const COMPONENT_ANALYTICS_EVENT_TYPES = ['impression', 'view', 'interaction'];

const SESSION_ID_KEY = 'muulti-component-analytics-session-id';
const ONCE_PREFIX = 'muulti-component-analytics-once';
const INTERACTION_THROTTLE_PREFIX = 'muulti-component-analytics-interaction-at';
const INTERACTION_THROTTLE_MS = 5000;

const safeString = (value) => String(value ?? '').trim();

export const getComponentAnalyticsComponentId = (comp, fallback = '') => (
    safeString(comp?.id) || safeString(fallback)
);

export const getComponentAnalyticsKey = ({ ownerPublicKey, componentId, eventType }) => (
    [safeString(ownerPublicKey), safeString(componentId), safeString(eventType)].join('__')
);

const getSessionStorage = () => {
    try {
        if (typeof window === 'undefined') return null;
        return window.sessionStorage || null;
    } catch {
        return null;
    }
};

const getCurrentPath = () => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname || ''}${window.location.search || ''}`;
};

export const getComponentAnalyticsSessionId = () => {
    const storage = getSessionStorage();
    if (!storage) return '';
    const existing = storage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    storage.setItem(SESSION_ID_KEY, generated);
    return generated;
};

const shouldSkipOnce = ({ eventType, ownerPublicKey, componentId, surface }) => {
    if (eventType === 'interaction') return false;
    const storage = getSessionStorage();
    if (!storage) return false;
    const key = `${ONCE_PREFIX}:${eventType}:${safeString(ownerPublicKey)}:${safeString(componentId)}:${safeString(surface)}`;
    if (storage.getItem(key)) return true;
    storage.setItem(key, '1');
    return false;
};

const shouldThrottleInteraction = ({ ownerPublicKey, componentId }) => {
    const storage = getSessionStorage();
    if (!storage) return false;
    const key = `${INTERACTION_THROTTLE_PREFIX}:${safeString(ownerPublicKey)}:${safeString(componentId)}`;
    const now = Date.now();
    const last = Number(storage.getItem(key) || 0);
    if (Number.isFinite(last) && now - last < INTERACTION_THROTTLE_MS) return true;
    storage.setItem(key, String(now));
    return false;
};

export const logComponentAnalyticsEvent = async ({
    eventType,
    comp,
    muulti,
    componentId: providedComponentId,
    surface = 'public',
}) => {
    const normalizedEventType = safeString(eventType);
    if (!COMPONENT_ANALYTICS_EVENT_TYPES.includes(normalizedEventType)) return;

    const ownerPublicKey = safeString(muulti?.publicKey || comp?.ownerUid || comp?.ownerPublicKey);
    const componentId = safeString(providedComponentId || getComponentAnalyticsComponentId(comp));
    if (!ownerPublicKey || !componentId) return;

    if (shouldSkipOnce({ eventType: normalizedEventType, ownerPublicKey, componentId, surface })) return;
    if (normalizedEventType === 'interaction' && shouldThrottleInteraction({ ownerPublicKey, componentId })) return;

    const componentType = safeString(comp?.type);
    const ownerUsername = safeString(muulti?.muultiName || comp?.muultiName);
    const analyticsKey = getComponentAnalyticsKey({ ownerPublicKey, componentId, eventType: normalizedEventType });

    try {
        await addDoc(collection(db, COMPONENT_ANALYTICS_EVENTS_COLLECTION), {
            analyticsKey,
            ownerPublicKey,
            ownerUsername,
            componentId,
            componentType,
            eventType: normalizedEventType,
            surface: safeString(surface) || 'public',
            sessionId: getComponentAnalyticsSessionId(),
            path: getCurrentPath(),
            createdAt: serverTimestamp(),
        });
    } catch (error) {
        console.warn('[component analytics] failed to log event:', error);
    }
};

export const getComponentAnalyticsCounts = async ({ ownerPublicKey, componentId }) => {
    const safeOwner = safeString(ownerPublicKey);
    const safeComponentId = safeString(componentId);
    if (!safeOwner || !safeComponentId) {
        return { impression: 0, view: 0, interaction: 0 };
    }

    const eventsRef = collection(db, COMPONENT_ANALYTICS_EVENTS_COLLECTION);
    const entries = await Promise.all(
        COMPONENT_ANALYTICS_EVENT_TYPES.map(async (eventType) => {
            const analyticsKey = getComponentAnalyticsKey({
                ownerPublicKey: safeOwner,
                componentId: safeComponentId,
                eventType,
            });
            const countQuery = query(eventsRef, where('analyticsKey', '==', analyticsKey));
            const snapshot = await getCountFromServer(countQuery);
            return [eventType, snapshot.data().count || 0];
        })
    );
    return Object.fromEntries(entries);
};
